import { useEffect, useRef, useState } from "react";
import type { DownloadContent } from "@dayvise/client-sdk";
import { useActiveWorkspaceId, useDayviseClient, useSession } from "./providers.tsx";

/** Explicit user downloads use the authenticated SDK, never a public artifact URL. */
export function saveFileContent(file: DownloadContent): void {
  const filename = file.filename.split(/[\\/]/).at(-1)?.replace(/[\u0000-\u001f\u007f]/g, "").trim() || "download";
  const url = URL.createObjectURL(new Blob([file.bytes], { type: file.mimeType }));
  const link = document.createElement("a");
  try {
    link.href = url;
    link.download = filename;
    link.style.display = "none";
    document.body.append(link);
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  } finally {
    link.remove();
  }
}
export function useFileDownload() {
  const client = useDayviseClient();
  const workspaceId = useActiveWorkspaceId();
  const { session } = useSession();
  const identity = JSON.stringify([workspaceId, session.account?.userId ?? null]);
  const current = useRef<string | null>(identity);
  const generation = useRef(0);
  const busy = useRef<string | null>(null);
  if (current.current !== identity) { generation.current++; busy.current = null; }
  current.current = identity;
  useEffect(() => {
    current.current = identity;
    return () => { current.current = null; generation.current++; busy.current = null; };
  }, [identity]);
  const [state, setState] = useState<{ identity: string; fileId: string | null; error: string | null }>({ identity, fileId: null, error: null });
  const download = async (fileId: string) => {
    if (busy.current === identity) return;
    busy.current = identity;
    const epoch = generation.current;
    const active = () => current.current === identity && generation.current === epoch;
    setState({ identity, fileId, error: null });
    try {
      const file = await client.files.download(fileId);
      if (active()) saveFileContent(file);
    } catch (error) {
      if (active()) setState({ identity, fileId: null, error: error instanceof Error ? error.message : "File download failed." });
      return;
    } finally {
      if (active()) busy.current = null;
    }
    if (active()) setState({ identity, fileId: null, error: null });
  };
  return { download, downloadingFileId: state.identity === identity ? state.fileId : null, error: state.identity === identity ? state.error : null };
}
